import React, { useEffect, useState, useCallback } from 'react';
import { FiRefreshCw, FiEdit2, FiCheck, FiX } from 'react-icons/fi';
import { buscarCodigosMunicipais, descreverCodigoMunicipal } from '../../services/nf';

/* ── Código complementar municipal (cTribMun) ──
   Os 3 dígitos que a prefeitura cadastrou no Sistema Nacional para o código
   de tributação nacional. A obrigatoriedade é do município: se ele não
   aceita o código "sem complemento" (.000), o campo é obrigatório.
   A API do Ambiente Nacional devolve só código e alíquota -- a descrição
   cada prefeitura publica na sua tabela. Quem descreve aqui uma vez,
   descreve pra todos os clientes daquele município e código.
   onInfo({ exige }) avisa quem precisa travar a emissão. */
function CampoCodigoMunicipal({ clienteId, ctn, municipio, valor, onChange, onInfo, className }) {
  const [info, setInfo] = useState(null);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState('');
  const [descrevendo, setDescrevendo] = useState(false);
  const [texto, setTexto] = useState('');
  const cod = String(ctn || '').replace(/\D/g, '');
  const mun = String(municipio || '').replace(/\D/g, '');

  const consultar = useCallback((atualizar) => {
    if (!clienteId || cod.length !== 6) { setInfo(null); onInfo?.({ exige: false }); return; }
    setCarregando(true); setErro('');
    buscarCodigosMunicipais(clienteId, cod, mun, atualizar)
      .then(r => { setInfo(r); onInfo?.({ exige: !!r.exige }); })
      .catch(e => { setInfo(null); setErro(e.message); onInfo?.({ exige: false }); })
      .finally(() => setCarregando(false));
  }, [clienteId, cod, mun]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { consultar(false); }, [consultar]);

  const opcoes = info?.codigos || [];
  const selecionado = opcoes.find(o => o.codigo === valor);
  const listaOk = !!info && !erro;
  /* município efetivo da consulta (o backend usa o do emissor quando vem vazio) */
  const munConsulta = mun || info?.municipioConsultado || '';

  async function salvarDescricao() {
    try {
      await descreverCodigoMunicipal({ municipio: munConsulta, ctn: cod, codigo: valor, descricao: texto });
      setDescrevendo(false);
      setInfo(i => ({ ...i, codigos: i.codigos.map(o => (o.codigo === valor ? { ...o, descricao: texto.trim() || null } : o)) }));
    } catch (e) { setErro(e.message); }
  }

  return (
    <div>
      <div style={{ display: 'flex', gap: 6 }}>
        {listaOk && (opcoes.length || info.semComplemento) ? (
          <select className={className} value={valor || ''} onChange={e => { onChange(e.target.value); setDescrevendo(false); }}
            style={info.exige && !valor ? { borderColor: '#dc2626' } : undefined}>
            <option value="">{info.exige ? 'Selecione...' : 'Sem complemento (o município não exige)'}</option>
            {opcoes.map(o => (
              <option key={o.codigo} value={o.codigo}>
                {o.codigo} - {o.descricao || 'sem descrição (clique no lápis para descrever)'}{o.aliquota != null ? ` · ISS ${String(o.aliquota).replace('.', ',')}%` : ''}
              </option>
            ))}
          </select>
        ) : (
          <input className={className} value={valor || ''} maxLength={3} inputMode="numeric" placeholder="3 dígitos (ex.: 001)"
            onChange={e => onChange(e.target.value.replace(/\D/g, '').slice(0, 3))} />
        )}
        {selecionado && munConsulta && (
          <button type="button" title="Descrever este código (vale para todos os clientes deste município)"
            onClick={() => { setTexto(selecionado.descricao || ''); setDescrevendo(v => !v); }}
            style={{ border: '1px solid #d7e3ef', background: '#fff', borderRadius: 8, padding: '0 10px', cursor: 'pointer', color: '#4A6B8A' }}>
            <FiEdit2 size={13} />
          </button>
        )}
        <button type="button" onClick={() => consultar(true)} disabled={carregando || cod.length !== 6}
          title="Consultar de novo no Sistema Nacional os códigos que o município aceita"
          style={{ border: '1px solid #d7e3ef', background: '#fff', borderRadius: 8, padding: '0 10px', cursor: 'pointer', color: '#4A6B8A' }}>
          <FiRefreshCw size={13} style={carregando ? { animation: 'girar 1s linear infinite' } : undefined} />
        </button>
      </div>
      {descrevendo && (
        <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
          <input className={className} autoFocus value={texto} maxLength={300} onChange={e => setTexto(e.target.value)}
            placeholder={`Descrição do ${valor} (copie da tabela de códigos da prefeitura)`}
            onKeyDown={e => { if (e.key === 'Enter') salvarDescricao(); if (e.key === 'Escape') setDescrevendo(false); }} />
          <button type="button" onClick={salvarDescricao} title="Salvar descrição"
            style={{ border: '1px solid #0D1F3C', background: '#0D1F3C', color: '#fff', borderRadius: 8, padding: '0 10px', cursor: 'pointer' }}><FiCheck size={13} /></button>
          <button type="button" onClick={() => setDescrevendo(false)} title="Cancelar"
            style={{ border: '1px solid #d7e3ef', background: '#fff', borderRadius: 8, padding: '0 10px', cursor: 'pointer', color: '#4A6B8A' }}><FiX size={13} /></button>
        </div>
      )}
      <small style={{ display: 'block', marginTop: 4, fontSize: 11.5, color: erro ? '#b91c1c' : info?.exige ? '#b45309' : '#6B8CAE' }}>
        {cod.length !== 6 ? 'Escolha primeiro o código de tributação nacional.'
          : carregando ? 'Consultando o município no Sistema Nacional...'
            : erro ? `Não foi possível consultar (${erro}). Digite o código se souber.`
              : info?.exige ? `Obrigatório neste município: ele não aceita o código sem complemento (${opcoes.length} opção(ões)).`
                : info?.semComplemento ? (opcoes.length ? 'Opcional: o município aceita o código sem complemento.' : 'Este município não usa complemento para este código.')
                  : 'O município não tem este código parametrizado no Sistema Nacional — confira o código de tributação nacional.'}
      </small>
    </div>
  );
}

export default CampoCodigoMunicipal;
